/** @prettier */

import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {Grid, Card, CardContent, Box, Stack} from '@mui/material';
import {useLoaderData, useLocation, useParams, Link} from 'react-router-dom';

//MTH component import
import OUAddDialog from './OUAddDialog';
import OUEditDialog from './OUEditDialog';
import OUDeleteDialog from './OUDeleteDialog';
import CardContents from '../../component/CardContents';
import GWConfigUpdateDrawer from '../gw-registration/GWConfigUpdateDrawer';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
//API import
import {
  getCompositionsGateway,
  getCompositionsMoubusUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack} from '../../define';
import {gwInfoSort} from '../../functions/sort';
import {sortByUnicode} from '../../functions/sort';
import {useOutlet} from '../gw-registration/GWMain';
import {checkUnitExistenceByGatewayID} from '../../functions/checkUnitExistence';

type LoaderReturnType = {
  /** サイトに登録されているゲートウェイの情報 */
  allGWInfo: apiIF.res.getCompositionsGateway;
  /** 表示しているゲートウェイが課金しているかどうか */
  isBilling: boolean;
  /** 表示しているゲートウェイに登録されている室内外機の情報 */
  modbusOUUnit: apiIF.res.getCompositionsMoubusUnit;
  /** 登録ユニットの有無 */
  hasUnit: boolean;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined || params.gwID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //ゲートウェイの情報を取得
    promises.push(getCompositionsGateway(params.siteID, {'max-result': 100}));
    //室外機の情報を取得
    promises.push(
      getCompositionsMoubusUnit(params.siteID, params.gwID, {'max-result': 400})
    );
    // ゲートウェイの機器登録状況を取得
    promises.push(checkUnitExistenceByGatewayID(params.siteID, params.gwID));
    const response = await Promise.all(promises);

    const allGWInfo = response[0] as apiIF.res.getCompositionsGateway;
    const modbusOUUnit = response[1] as apiIF.res.getCompositionsMoubusUnit;
    /** ユニットの登録状況 */
    const unitExistence = response[2] as boolean;

    //課金状況の判定
    let isBilling = false;
    const displayGWInfo = allGWInfo['gateway-informations'].find(gwInfo => {
      return gwInfo['gateway-id'] === params.gwID;
    });
    if (
      displayGWInfo !== undefined &&
      displayGWInfo['billing-status'] !== undefined
    ) {
      isBilling = displayGWInfo['billing-status'] !== 'unpaid';
    }

    //表示内容をUnicode順に並び替え
    modbusOUUnit['outdoor-unit-informations'].sort(sortByUnicode('unit-name'));
    return {
      allGWInfo: gwInfoSort(allGWInfo),
      isBilling: isBilling,
      modbusOUUnit: modbusOUUnit,
      hasUnit: unitExistence as boolean,
    };
  } catch {
    return undefined;
  }
}

/**
 * Modbus通信設定の室外機一覧
 * @returns JSX
 */
const OURegistration = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {siteID, gwID, systemType} = useParams();
  //親コンポーネントから値を取得
  const {setAllGWInfo} = useOutlet();

  const [isBilling, setIsBilling] = useState(false); //表示しているゲートウェイが課金しているかどうか
  //prettier-ignore
  const [modbusInfo, setModbusInfo] = useState<apiIF.res.getCompositionsMoubusUnit | null>(null); //Modbus空調機の情報
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [selectedOUID, setSelectedOUID] = useState(''); //選択した室外機のID
  const [hasConfigGW, setHasConfigGW] = useState<boolean>(false); //GW毎の構成情報未反映の有無
  const [hasUnit, setHasUnit] = useState<boolean>(false); //GWの機器登録有無

  //電源の種類変換テーブル
  const powerSupplyTable: {[key: string]: string} = {
    singlePhase: trans(
      'OURegistration.modbusUnit.item.value.powerSupply.singlePhase'
    ),
    threePhase: trans(
      'OURegistration.modbusUnit.item.value.powerSupply.threePhase'
    ),
  };
  /** 室外機追加ボタンの名前 */
  const addButtonName = trans('OUAddDialog.modbus.buttonTxt.addHccvq');

  useEffect(() => {
    if (loaderData !== undefined) {
      setAllGWInfo(loaderData['allGWInfo']);
      setIsBilling(loaderData.isBilling);
      setModbusInfo(loaderData['modbusOUUnit']);
      setHasUnit(loaderData.hasUnit);

      //構成情報未反映の有無をチェック
      for (const gwInfo of loaderData['allGWInfo']['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        setHasConfigGW(
          loaderData.isBilling &&
            gwInfo['composition-receipt-version'] !== undefined &&
            gwInfo['composition-receipt-version'] !== 'latest'
        );
        break;
      }
    }
  }, [loaderData, gwID, setAllGWInfo]);

  //空調機の情報が更新された場合、ツリービューを更新
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    //ゲートウェイの情報を取得
    getCompositionsGateway(siteID, {'max-result': 100}).then(response => {
      //表示内容をUnicode順に並び替え
      response = gwInfoSort(response);
      setAllGWInfo(response);
      //ユニットの有無をチェック
      for (const gwInfo of response['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        checkUnitExistenceByGatewayID(siteID, gwID).then(response => {
          setHasUnit(response as boolean);
        });
        break;
      }
    });
  }, [modbusInfo, siteID, setAllGWInfo]);

  /**
   * Editボタン押下時処理
   * @param ouID 室外機ID
   */
  const openEditDialog = (ouID: string) => {
    setIsOpenEditDialog(true);
    setSelectedOUID(ouID);
  };
  /**
   * Deleteボタン押下時処理
   * @param ouID 室外機ID
   */
  const openDeleteDialog = (ouID: string) => {
    setIsOpenDeleteDialog(true);
    setSelectedOUID(ouID);
  };

  /**
   * 歯車メニューリスト
   * @param ouID 室外機ID
   * @returns メニュー一覧
   */
  const gearMenuItem = (ouID: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        label: trans('OURegistration.modbusUnit.menuItem.edit'),
        onClick: () => openEditDialog(ouID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `OURegistration.modbusUnit.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
      {
        label: trans('OURegistration.modbusUnit.menuItem.delete'),
        onClick: () => openDeleteDialog(ouID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.DELETE.src,
          alt: trans(
            `OURegistration.modbusUnit.image.alternativeText.${iconBlack.DELETE.alt}`
          ),
        },
      },
    ];
  };

  //パスパラメータを取得できない場合
  //Modbus空調機の情報を取得できない場合
  if (modbusInfo === null || systemType === undefined) {
    return <></>;
  }
  return (
    <>
      <Box className="gwSetting__title">
        {trans(`OURegistration.modbusUnit.title.${systemType}`)}
      </Box>
      <Stack spacing={4}>
        {/* 室外機追加ボタン */}
        <Box className="gwSetting__contents--unit gwSetting__contents__button--add">
          <OUAddDialog
            addButtonName={addButtonName}
            modbusInfo={modbusInfo}
            setModbusInfo={setModbusInfo}
            isBilling={isBilling}
            setHasConfigGW={setHasConfigGW}
          />
        </Box>
        {/* 登録済室外機が無い場合は、室外機追加ボタンのみ表示 */}
        {modbusInfo['outdoor-unit-informations'].length === 0 ? (
          <Card className="gwSetting__contents--unit">
            <CardContent className="gwSetting__contents__noRegister">
              {trans('OURegistration.modbusUnit.instruction', {
                name: addButtonName,
              })}
            </CardContent>
          </Card>
        ) : (
          //登録済み室外機
          modbusInfo['outdoor-unit-informations'].map(ouInfo => (
            <Card
              id={`ouBox-${ouInfo['outdoor-id']}`}
              key={`ouBox-${ouInfo['outdoor-id']}`}
              className="gwSetting__contents--unit">
              <CardContent>
                <Stack spacing={2}>
                  {/* 室外機名称と歯車 */}
                  <Grid
                    container
                    justifyContent="space-between"
                    className="gwSetting__contents__title">
                    {/* 室外機名称 */}
                    <Grid item xs="auto">
                      <Link
                        to={`${location.pathname}/${ouInfo['outdoor-id']}/modbus-iu-registration${location.search}`}
                        className="linkIuSetting">
                        {ouInfo['outdoor-unit-name']}
                      </Link>
                    </Grid>
                    {/* 歯車ボタン */}
                    <Grid item xs={1}>
                      <GearMenu
                        id={`mv-btnModbusOUSettingOpen${ouInfo['outdoor-id']}`}
                        menuItem={gearMenuItem(ouInfo['outdoor-id'])}
                      />
                      {/* 選択した室外機のダイアログのみ出す */}
                      {ouInfo['outdoor-id'] === selectedOUID &&
                      ouInfo['outdoor-unit-name'] !== undefined ? (
                        <>
                          <OUEditDialog
                            ouID={ouInfo['outdoor-id']}
                            displayOUInfo={ouInfo}
                            setModbusInfo={setModbusInfo}
                            isOpenDialog={isOpenEditDialog}
                            setIsOpenDialog={setIsOpenEditDialog}
                            isBilling={isBilling}
                            setHasConfigGW={setHasConfigGW}
                          />
                          <OUDeleteDialog
                            ouID={ouInfo['outdoor-id']}
                            ouName={ouInfo['outdoor-unit-name']}
                            setModbusInfo={setModbusInfo}
                            isOpenDialog={isOpenDeleteDialog}
                            setIsOpenDialog={setIsOpenDeleteDialog}
                            isBilling={isBilling}
                            setHasConfigGW={setHasConfigGW}
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                  {/* 室外機の情報 */}
                  <Stack spacing={2} className="gwSetting__contents__contents">
                    {/* 室外機タイプ */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OURegistration.modbusUnit.item.title.unitType'
                      )}
                      value={
                        ouInfo['outdoor-unit-type'] === undefined
                          ? ''
                          : `${trans(
                              `OURegistration.modbusUnit.item.value.unitType.${ouInfo[
                                'outdoor-unit-type'
                              ].toLowerCase()}`
                            )}`
                      }
                    />
                    {/* IPアドレス */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OURegistration.modbusUnit.item.title.ipAddress'
                      )}
                      value={
                        ouInfo['ip-address'] === undefined
                          ? ''
                          : ouInfo['ip-address']
                      }
                    />
                    {/* 入力電圧 */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OURegistration.modbusUnit.item.title.voltage'
                      )}
                      value={
                        ouInfo['input-voltage'] === undefined
                          ? ''
                          : ouInfo['input-voltage']
                      }
                    />
                    {/* 電源の種類 */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OURegistration.modbusUnit.item.title.powerSupply'
                      )}
                      value={
                        ouInfo['power-supply'] === undefined
                          ? ''
                          : powerSupplyTable[ouInfo['power-supply']]
                      }
                    />
                    {/* 接続室内機 */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OURegistration.modbusUnit.item.title.indoorUnit'
                      )}
                      value={
                        ouInfo['indoor-unit-informations'] === undefined ? (
                          <></>
                        ) : (
                          <Stack spacing={0}>
                            {ouInfo['indoor-unit-informations'].map(iuInfo => (
                              <Box key={iuInfo['unit-id']}>
                                {iuInfo['indoor-unit-name']}
                              </Box>
                            ))}
                          </Stack>
                        )
                      }
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
      {/* 構成情報反映ボタン */}
      <GWConfigUpdateDrawer
        hasConfigGW={hasConfigGW}
        setHasConfigGW={setHasConfigGW}
        hasUnit={hasUnit}
      />
    </>
  );
};
export default OURegistration;
